import type { EntityScreenMode } from '../utils/entityScreenRouteUtils';

/** Route sections that map to URL prefixes */
export type RouteSection = 'person' | 'admin' | 'other' | 'meta' | 'localities';

/** All utility page names (for #utilities/xxx routes) */
export type UtilityPage =
    | 'testing' | 'jsTesting' | 'customFilter' | 'patchTesting'
    | 'scimSelect' | 'speedComparison' | 'searchCompare' | 'encdec'
    | 'setGen' | 'policyRuleAudit' | 'contentTypes' | 'searchIndexes'
    | 'userSettings' | 'freeEdit';

export interface DashboardRoute { type: 'dashboard'; }

export interface EntityRoute {
    type: 'entity';
    section: RouteSection;
    entityType: string;
    entityId?: string | null;
    mode?: EntityScreenMode;
}

export interface GroupsRoute {
    type: 'groups';
    entityId?: string | null;
    mode?: EntityScreenMode;
}

export interface ClassesRoute {
    type: 'classes';
    entityId?: string | null;
    mode?: EntityScreenMode;
}

export interface PluginRoute {
    type: 'plugin';
    sub: 'invoke' | 'table';
    pluginId?: string | null;
    pivotEntityId?: string | null;
    pivotDetailId?: string | null;
    prepareCatalogId?: string | null;
    prepareDetailId?: string | null;
    entityId?: string | null;
    mode?: EntityScreenMode;
}

export interface UtilityRoute {
    type: 'utility';
    page: UtilityPage;
}

export interface SpecialRoute {
    type: 'special';
    page: 'schoolMap' | 'serviceProviderConfig' | 'resourceTypes';
}

export type AppRoute =
    | DashboardRoute
    | EntityRoute
    | GroupsRoute
    | ClassesRoute
    | PluginRoute
    | UtilityRoute
    | SpecialRoute;

export const DASHBOARD_ROUTE: DashboardRoute = { type: 'dashboard' };

/**
 * Helper to extract selectedDestination string from an AppRoute
 * (backwards compat with existing code that uses selectedDestination).
 */
export function routeToDestination(route: AppRoute): string {
    switch (route.type) {
        case 'dashboard': return 'dashboard';
        case 'groups': return 'groups';
        case 'classes': return 'classes';
        case 'utility': return route.page;
        case 'special': return route.page;
        case 'plugin': return `admin/pluginfunctions/${route.sub}`;
        case 'entity': {
            const cap = route.entityType.charAt(0).toUpperCase() + route.entityType.slice(1);
            if (route.section === 'person') return 'Person';
            if (route.section === 'localities') return `localities/${cap}`;
            return `${route.section}/${cap}`;
        }
    }
}

/** Extract entity type string from an AppRoute (or undefined for non-entity routes) */
export function routeToEntityType(route: AppRoute): string | undefined {
    switch (route.type) {
        case 'entity': return route.entityType;
        case 'groups': return 'Group';
        case 'classes': return 'Class';
        default: return undefined;
    }
}

/** Extract entity ID from an AppRoute */
export function routeToEntityId(route: AppRoute): string | null {
    switch (route.type) {
        case 'entity': return route.entityId ?? null;
        case 'groups': return route.entityId ?? null;
        case 'classes': return route.entityId ?? null;
        case 'plugin': return route.entityId ?? null;
        default: return null;
    }
}

/** Extract view mode from an AppRoute */
export function routeToViewMode(route: AppRoute): EntityScreenMode {
    switch (route.type) {
        case 'entity': return route.mode ?? null;
        case 'groups': return route.mode ?? null;
        case 'classes': return route.mode ?? null;
        case 'plugin': return route.mode ?? null;
        default: return null;
    }
}

/** Extract entity category for render decisions */
export function routeToCategory(route: AppRoute): 'person' | 'locality' | 'other' | 'meta' | 'utilities' | 'admin' | null {
    switch (route.type) {
        case 'entity':
            if (route.section === 'person') return 'person';
            if (route.section === 'localities') return 'locality';
            if (route.section === 'admin') return 'admin';
            if (route.section === 'meta') return 'meta';
            if (route.section === 'other') return 'other';
            return null;
        case 'utility': return 'utilities';
        case 'special': return 'meta';
        default: return null;
    }
}
